import type { IssueSeverity } from '@/shared/constants/issue-status'

import { useGisCoverageView } from '../stores/gisCoverageViewStore'
import { useGisIssueView } from '../stores/gisIssueViewStore'

import type { GisTab, SiteStatus } from '../api/types'
import type { DashFilter } from '../lib/dashboard-filter'

/** The dashboard's drill actions: apply the matching filter on the target board's store, then jump
 *  to it. Shared by the Dashboard tab and the per-board dashboard overlays. */
export interface BoardShortcuts {
  goIssues: (sev?: IssueSeverity) => void
  /** The whole window's feed — All statuses (the "Total issues" KPI). */
  goIssuesAll: () => void
  /** The Closed view (the "Completed" KPI). */
  goIssuesClosed: () => void
  /** Open issues grouped by their site — the "Issues in each site" KPI link. */
  goIssuesBySite: () => void
  /** Filter the Issues board to one site (via the search query) and open it. */
  goIssuesSite: (siteName: string) => void
  /** Apply a pending-age band filter (the dashboard "Show filters" hand-off) and open the board. */
  goIssuesAge: (bands: readonly string[]) => void
  /** Apply the dashboard's whole staged filter to the board and open it (the "Show issues" action). */
  goIssuesFiltered: (filter: DashFilter) => void
  goIssuesCategory: (category: string) => void
  goIssuesDept: (department: string) => void
  /** Filter via the HOD facet (values are department names — HOD ↔ department is 1:1). */
  goIssuesHod: (department: string) => void
  goIssuesByQuestion: () => void
  /** Scope the page to a mandal (taluka) and open the Issues board on it. */
  goIssuesMandal: (name: string) => void
  goCoverage: (status?: SiteStatus) => void
  goCoverageMandal: (name: string) => void
  goCoverageOfficers: () => void
}

interface UseBoardShortcutsArgs {
  /** Navigate to a board tab. Overlay callers wrap this to also close their window first. */
  onOpenLens: (tab: GisTab) => void
  /** Toggle a mandal into the URL scope (used by the coverage-by-mandal jump). */
  onSelectMandal: (name: string) => void
  activeMandals: readonly string[]
}

/**
 * Build the dashboard→board shortcut actions. Guards keep every toggle idempotent — a second click
 * on the same tile/arc/row must not un-filter behind the user's back.
 */
export function useBoardShortcuts({ onOpenLens, onSelectMandal, activeMandals }: UseBoardShortcutsArgs): BoardShortcuts {
  const issueView = useGisIssueView
  const severity = issueView((s) => s.severity)
  const category = issueView((s) => s.category)
  const department = issueView((s) => s.department)
  const hod = issueView((s) => s.hod)
  const setStatusView = issueView((s) => s.setStatusView)
  const toggleSeverity = issueView((s) => s.toggleSeverity)
  const toggleCategory = issueView((s) => s.toggleCategory)
  const toggleDepartment = issueView((s) => s.toggleDepartment)
  const toggleHod = issueView((s) => s.toggleHod)
  const setIssueGroupBy = issueView((s) => s.setGroupBy)
  const setIssueQuery = issueView((s) => s.setQuery)
  const setAgeBands = issueView((s) => s.setAgeBands)
  const applyFilters = issueView((s) => s.applyFilters)
  const covView = useGisCoverageView
  const covStatus = covView((s) => s.statusFilter)
  const toggleCovStatus = covView((s) => s.toggleStatusFilter)
  const setListMode = covView((s) => s.setListMode)

  const goIssues = (sev?: IssueSeverity): void => {
    setStatusView('open')
    if (sev && !severity.includes(sev)) {
      toggleSeverity(sev)
    }
    onOpenLens('issues')
  }
  const goCoverage = (status?: SiteStatus): void => {
    if (status && !covStatus.includes(status)) {
      toggleCovStatus(status)
    }
    setListMode('sites')
    onOpenLens('coverage')
  }

  return {
    goIssues,
    goIssuesAll: () => {
      setStatusView('all')
      onOpenLens('issues')
    },
    goIssuesClosed: () => {
      setStatusView('closed')
      onOpenLens('issues')
    },
    goIssuesBySite: () => {
      setStatusView('open')
      setIssueGroupBy('site')
      onOpenLens('issues')
    },
    goIssuesSite: (name) => {
      setStatusView('open')
      setIssueQuery(name)
      onOpenLens('issues')
    },
    goIssuesAge: (bands) => {
      setStatusView('open')
      setAgeBands([...bands])
      onOpenLens('issues')
    },
    goIssuesFiltered: (filter) => {
      setStatusView('open')
      applyFilters({
        severity: filter.severity,
        category: filter.category,
        department: filter.department,
        hod: filter.hod,
        site: filter.site,
        age: filter.age,
      })
      onOpenLens('issues')
    },
    goIssuesCategory: (cat) => {
      setStatusView('open')
      if (!category.includes(cat)) {
        toggleCategory(cat)
      }
      onOpenLens('issues')
    },
    goIssuesDept: (dept) => {
      setStatusView('open')
      if (!department.includes(dept)) {
        toggleDepartment(dept)
      }
      onOpenLens('issues')
    },
    goIssuesHod: (dept) => {
      setStatusView('open')
      if (!hod.includes(dept)) {
        toggleHod(dept)
      }
      onOpenLens('issues')
    },
    goIssuesByQuestion: () => {
      setStatusView('open')
      setIssueGroupBy('question')
      onOpenLens('issues')
    },
    goIssuesMandal: (name) => {
      if (!activeMandals.includes(name)) {
        onSelectMandal(name)
      }
      setStatusView('open')
      onOpenLens('issues')
    },
    goCoverage,
    goCoverageMandal: (name) => {
      if (!activeMandals.includes(name)) {
        onSelectMandal(name)
      }
      setListMode('sites')
      onOpenLens('coverage')
    },
    goCoverageOfficers: () => {
      setListMode('officers')
      onOpenLens('coverage')
    },
  }
}
